import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAdmin } from '../../context/AdminContext';
import { Lock, Envelope, Eye, EyeSlash, WarningCircle, Spinner, ArrowLeft } from '@phosphor-icons/react';
import logoPath from '../../logo.webp';

export const AdminLogin: React.FC = () => {
   const { login, user, isLoading, isConfigured } = useAdmin();
   const navigate = useNavigate();

   const [email, setEmail] = useState('');
   const [password, setPassword] = useState('');
   const [showPassword, setShowPassword] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [isSubmitting, setIsSubmitting] = useState(false);

   // Redirect if already logged in
   useEffect(() => {
      if (user && !isLoading) {
         navigate('/admin/dashboard');
      }
   }, [user, isLoading, navigate]);

   const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);

      if (!email.trim() || !password.trim()) {
         setError('Please enter both email and password');
         return;
      }

      setIsSubmitting(true);

      try {
         const result = await login(email, password);

         if (result.success) {
            navigate('/admin/dashboard');
         } else {
            setError(result.error || 'Failed to sign in');
         }
      } catch (err: any) {
         setError(err.message || 'An unexpected error occurred');
      } finally {
         setIsSubmitting(false);
      }
   };

   if (isLoading) {
      return (
         <div className="min-h-screen bg-brand-950 flex items-center justify-center">
            <Spinner className="w-8 h-8 text-brand-500 animate-spin" />
         </div>
      );
   }

   return (
      <div className="min-h-screen bg-brand-950 flex items-center justify-center p-4 relative overflow-hidden">
         {/* Background Elements */}
         <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
            <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-brand-500/10 rounded-full blur-[100px]" />
            <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-blue-500/10 rounded-full blur-[100px]" />
         </div>

         {/* Back to Home Link */}
         <Link
            to="/"
            className="absolute top-6 left-6 flex items-center gap-2 text-gray-400 hover:text-white transition-colors group z-10"
         >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span className="font-medium">Back to Home</span>
         </Link>

         <div className="w-full max-w-md relative z-10">
            {/* Logo */}
            <div className="text-center mb-8">
               <div className="mx-auto w-28 mb-2">
                  {/* Small logo image - bundled */}
                  <img src={logoPath} alt="Nida logo" className="w-full h-auto mx-auto" />
               </div>
               <p className="text-gray-400 mt-2">Admin Portal</p>
            </div>

            {/* Login Card */}
            <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-8 shadow-2xl border border-white/10">
               <div className="flex items-center justify-center w-16 h-16 bg-brand-500/10 rounded-full mx-auto mb-6">
                  <Lock className="w-8 h-8 text-brand-500" weight="duotone" />
               </div>

               <h2 className="text-2xl font-bold text-white text-center mb-2">
                  Welcome Back
               </h2>
               <p className="text-gray-400 text-center mb-8">
                  Sign in to access the admin dashboard
               </p>

               {/* Configuration Warning */}
               {!isConfigured && (
                  <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                     <div className="flex items-start gap-3">
                        <WarningCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                        <div>
                           <p className="text-yellow-200 text-sm font-medium">Database Not Configured</p>
                           <p className="text-yellow-300/70 text-xs mt-1">
                              Please add your Supabase credentials to .env.local to enable authentication.
                           </p>
                        </div>
                     </div>
                  </div>
               )}

               {/* Error Message */}
               {error && (
                  <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                     <div className="flex items-center gap-3">
                        <WarningCircle className="w-5 h-5 text-red-400" />
                        <p className="text-red-200 text-sm">{error}</p>
                     </div>
                  </div>
               )}

               <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Email Field */}
                  <div>
                     <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                        Email Address
                     </label>
                     <div className="relative">
                        <Envelope className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                        <input
                           id="email"
                           type="email"
                           value={email}
                           onChange={(e) => setEmail(e.target.value)}
                           placeholder="you@example.com"
                           className="w-full pl-11 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                           disabled={isSubmitting}
                        />
                     </div>
                  </div>

                  {/* Password Field */}
                  <div>
                     <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                        Password
                     </label>
                     <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                        <input
                           id="password"
                           type={showPassword ? 'text' : 'password'}
                           value={password}
                           onChange={(e) => setPassword(e.target.value)}
                           placeholder="Enter your password"
                           className="w-full pl-11 pr-12 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                           disabled={isSubmitting}
                        />
                        <button
                           type="button"
                           onClick={() => setShowPassword(!showPassword)}
                           className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                        >
                           {showPassword ? <EyeSlash className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                     </div>
                  </div>

                  {/* Submit Button */}
                  <button
                     type="submit"
                     disabled={isSubmitting || !isConfigured}
                     className="w-full py-3 bg-brand-500 hover:bg-brand-600 disabled:bg-gray-800 disabled:text-gray-500 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-brand-500/25"
                  >
                     {isSubmitting ? (
                        <>
                           <Spinner className="w-5 h-5 animate-spin" />
                           Signing in...
                        </>
                     ) : (
                        'Sign In'
                     )}
                  </button>
               </form>

               {/* Help Text */}
               <div className="mt-6 pt-6 border-t border-white/10">
                  <p className="text-gray-400 text-sm text-center">
                     Need access?{' '}
                     <a href="mailto:admin@nidastudio.com" className="text-brand-500 hover:text-brand-400 transition-colors">
                        Contact your administrator
                     </a>
                  </p>
               </div>
            </div>

            {/* Footer */}
               <p className="text-gray-500 text-xs text-center mt-8">
                  © {new Date().getFullYear()} Nida Studios. All rights reserved.
               </p>
         </div>
      </div>
   );
};